import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card } from '@/components/ui/Card';
import { TextField, PasswordField } from '@/components/ui/TextField';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { errorMessage } from '@/lib/errorMessage';
import { useT } from '@/i18n/useT';
import { VerificationPanel } from './VerificationPanel';
import {
  useLinkStatus,
  useTenantStatus,
  useProvisionBot,
  useLinkAccount,
  useUnlinkAccount,
} from '@/tenancy/hooks';

interface Form {
  poEmail: string;
  poPassword: string;
  accountType: 'demo' | 'real';
}

/**
 * Phase 4B-1 — "Connect your Pocket Option account".
 *
 * Deliberately a status-driven screen rather than a form that submits once:
 * Pocket Option challenges new-device logins with a CAPTCHA or an emailed code,
 * so "not connected yet" is a normal state the customer has to be able to see
 * and act on. The password is sent once and never rendered back.
 */
export function ConnectPage() {
  const { t } = useT();
  const tenant = useTenantStatus();
  const link = useLinkStatus();
  const provision = useProvisionBot();
  const connect = useLinkAccount();
  const disconnect = useUnlinkAccount();
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Form>({ defaultValues: { accountType: 'demo' } });

  const state = link.data?.state ?? 'not_provisioned';
  const busy = provision.isPending || connect.isPending || disconnect.isPending;

  const onCreate = async () => {
    setError('');
    try {
      await provision.mutateAsync();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const onConnect = handleSubmit(async (data) => {
    setError('');
    try {
      const r = await connect.mutateAsync(data);
      // Never keep the password in component state or the form after submit.
      reset({ poEmail: data.poEmail, poPassword: '', accountType: data.accountType });
      if (r.state === 'failed') setError(r.detail);
    } catch (e) {
      setError(errorMessage(e));
    }
  });

  const onDisconnect = async () => {
    setError('');
    try {
      await disconnect.mutateAsync();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  if (tenant.isLoading || link.isLoading) {
    return (
      <Card title={t('bot.title')} subtitle={t('bot.subtitle')}>
        <p className="muted">{t('common.loading')}</p>
      </Card>
    );
  }

  // No bot yet: subscribing creates the account, this creates the machine.
  if (!tenant.data?.hasTenant || state === 'not_provisioned') {
    return (
      <Card title={t('bot.title')} subtitle={t('bot.subtitle')}>
        <Alert kind="error">{error}</Alert>
        <p className="muted">{t('bot.createBlurb')}</p>
        <Button onClick={onCreate} busy={provision.isPending}>
          {provision.isPending ? t('bot.creating') : t('bot.createCta')}
        </Button>
      </Card>
    );
  }

  return (
    <Card title={t('bot.title')} subtitle={t('bot.subtitle')}>
      <Alert kind="error">{error}</Alert>

      {state === 'linked' && (
        <>
          <Alert kind="success">{t('bot.connected', { email: link.data?.poEmail ?? '' })}</Alert>
          <p className="muted">{t('bot.connectedBlurb')}</p>
          <Button variant="ghost" onClick={onDisconnect} busy={disconnect.isPending}>
            {disconnect.isPending ? t('bot.disconnecting') : t('bot.disconnectCta')}
          </Button>
        </>
      )}

      {state === 'linking' && (
        <div className="billing-trial-live">
          <span className="billing-trial-dot" />
          <div>
            <div className="billing-trial-count">{t('bot.connecting')}</div>
            <div className="muted">{link.data?.detail}</div>
          </div>
        </div>
      )}

      {/* Pocket Option challenged the login. Auto-retry cannot solve a robot
          check, and until Feature A this said "support has been alerted" and
          left the customer waiting for a human. Now they can do it themselves,
          in their own bot's browser, without anyone touching SSH. */}
      {state === 'verification_required' && (
        <>
          <Alert kind="error">{t('bot.verificationTitle')}</Alert>
          {verifying ? (
            <VerificationPanel onClose={() => setVerifying(false)} />
          ) : (
            <>
              <p className="muted">{t('bot.verifyBlurb')}</p>
              <Button onClick={() => setVerifying(true)}>{t('bot.verifyCta')}</Button>
            </>
          )}
        </>
      )}

      {(state === 'credentials_needed' || state === 'failed') && (
        <>
          {state === 'failed' && link.data?.detail ? <Alert kind="error">{link.data.detail}</Alert> : null}
          <p className="muted">{t('bot.connectBlurb')}</p>
          <form onSubmit={onConnect} noValidate>
            <TextField
              label={t('bot.poEmail')}
              type="email"
              autoComplete="off"
              error={errors.poEmail?.message}
              disabled={busy}
              {...register('poEmail', { required: t('validation.required') })}
            />
            <PasswordField
              label={t('bot.poPassword')}
              autoComplete="off"
              hint={t('bot.passwordHint')}
              error={errors.poPassword?.message}
              disabled={busy}
              {...register('poPassword', { required: t('validation.required') })}
            />
            <label className="field">
              <span className="field-label">{t('bot.accountType')}</span>
              <select className="input" disabled={busy} {...register('accountType')}>
                <option value="demo">{t('bot.demo')}</option>
                <option value="real">{t('bot.real')}</option>
              </select>
            </label>
            <Button type="submit" busy={connect.isPending}>
              {connect.isPending ? t('bot.connectingCta') : t('bot.connectCta')}
            </Button>
          </form>
          <p className="billing-fineprint muted">{t('bot.securityNote')}</p>
        </>
      )}
    </Card>
  );
}
